import React, { useEffect, useRef, useState } from 'react';
import { BrowserRouter as Router, Route, Redirect, Switch, useHistory, useLocation } from 'react-router-dom';
import 'react-toastify/dist/ReactToastify.css';
import 'animate.css/animate.css';
import { toast } from 'react-toastify';

import { gql, ApolloProvider, useQuery, useLazyQuery } from '@apollo/client';

import Profile from './screens/Profile';
import Vacations from './screens/Vacations';
import Colleagues from './screens/Colleagues';
import Authentication from './screens/Authentication';
import Expenses from './screens/Expenses';
import LoginPage from './screens/Login/LoginPage';
import MobileBuilds from './screens/MobileBuilds';
import Invoices from './screens/Invoices';
import InvoiceDetail from './screens/InvoiceDetail';
import Orders from './screens/Orders';
import OrderDetail from './screens/OrderDetail';
import ErrorPage from './components/ErrorPage/ErrorPage';
import Team from './screens/Team';
import Admin from './screens/Admin';
import LanguageSwitcher from './components/LanguageSwitcher/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import useLogOut from 'Shared/hooks/useLogOut';
import AppSwitch from 'Components/AppSwitch';

import jsCookie from 'js-cookie';
import ReactGA from 'Shared/analytics';
import { queryGetProfileData } from './screens/Profile/Profile';
import _ from 'lodash';

import 'react-toastify/dist/ReactToastify.css';
import 'animate.css/animate.css';

import Client from './shared/apollo/environment/environment';
import { StoreProvider } from './screens/shared/context/StoreContext';
import withErrorBoundary from 'Components/ErrorBoundary/ErrorBoundary';
import { ADMIN_URL } from './screens/Admin/constants/constants';

const RedirectCustom = ({ profileData, logOut }) => {
  if (profileData.user) {
    const { customerprofile, employeeprofile, permissions } = profileData.user;

    if (customerprofile) return <Redirect to="customer/invoices/open/" />;
    if (employeeprofile) {
      if (_.includes(permissions, 'can_add_leaverequest')) return <Redirect to="employee/vacations/leave-request/" />;
      else if (_.includes(permissions, 'can_add_expenserequest')) return <Redirect to="employee/expenses/request/" />;
      else return <Redirect to="employee/colleagues/contacts/" />;
    }
  }

  if (profileData.user === null) {
    logOut();
  }
};

const getTabIndicatioMetaInfo = gql`
  query TabIndicationQuery($contractId: Int!) @api(name: root) {
    orderMetaInfo(contractId: $contractId) {
      quotations
      inProgress
      completed
      requested
    }
    invoiceMetaInfo(contractId: $contractId) {
      open
      paid
    }
  }
`;

const user_id = jsCookie.get('user_id');
// GA_CODE is the GA4 Measurement ID, injected per deployment (.gitlab-ci.yml).
// The wrapper no-ops when it's unset (e.g. local dev without GA_CODE).
ReactGA.initialize(process.env.GA_CODE, {
  gaOptions: {
    userId: user_id,
  },
  // We send page_view ourselves on every route change (see the useLocation
  // effect below), so disable gtag's automatic page_view to avoid double counts.
  gtagOptions: {
    send_page_view: false,
  },
});

export const App = withErrorBoundary(() => {
  const [contract, setContract] = useState(null);
  const [permissions, setPermissions] = useState([]);
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const [logOut] = useLogOut();

  // Send a GA4 page_view on every client-side route change (baseline coverage
  // for all routes; individual screens may still fire their own pageviews).
  useEffect(() => {
    ReactGA.pageview(location.pathname + location.search);
  }, [location.pathname, location.search]);

  toast.configure({
    autoClose: 3000,
    draggable: false,
    position: 'top-center',
    className: 'notification',
  });

  const skip = useRef(true);

  if (jsCookie.get('isAuthenticated') && skip.current) skip.current = false;

  const { data: profileData, refetch, error } = useQuery(queryGetProfileData, {
    skip: skip.current,
    onCompleted(data) {
      setPermissions(data.user.permissions);
    },
  });

  const [getIndication, { data: indication, refetch: refetchIndication }] = useLazyQuery(getTabIndicatioMetaInfo, {
    variables: { contractId: +contract?.id },
  });

  const counterError = useRef(0);
  const initRequest = useRef(true);

  if (error && counterError.current < 10) {
    if (error.message.indexOf('status code 400') !== -1) {
      history.push('/400-error/');
      counterError.current = 10;
    }
    if (error.message.indexOf('status code 500') !== -1) {
      history.push('/500-error/');
      counterError.current = 10;
    }
    counterError.current += 1;
    refetch();
  }

  if (contract && initRequest.current && profileData?.user.customerprofile) {
    getIndication();
    initRequest.current = false;
  }

  return (
    <StoreProvider value={{ contract, permissions, setContract, refetchIndication }}>
      <AppSwitch>
        <Route path="/login/:props" component={LoginPage} />
        <Route
          path="/"
          exact
          render={() => {
            if (profileData) {
              return <RedirectCustom profileData={profileData} logOut={logOut} />;
            }
          }}
        />
        <Route
          path="/employee/vacations"
          render={(routeProps) => <Vacations profileData={profileData} {...routeProps} />}
        />
        <Route
          path="/employee/expenses"
          render={(routeProps) => <Expenses profileData={profileData} {...routeProps} />}
        />
        <Route
          path="/employee/colleagues"
          render={(routeProps) => <Colleagues profileData={profileData} {...routeProps} />}
        />
        <Route
          path="/employee/mobile-builds/"
          render={(routeProps) => <MobileBuilds profileData={profileData} {...routeProps} />}
        />
        <Route
          path="/customer/invoices"
          render={(routeProps) => <Invoices profileData={profileData} indicationTab={indication} {...routeProps} />}
        />
        <Route
          path="/customer/invoice"
          render={(routeProps) => <InvoiceDetail profileData={profileData} {...routeProps} />}
        />
        <Route
          path="/customer/orders"
          render={(routeProps) => <Orders profileData={profileData} indicationTab={indication} {...routeProps} />}
        />
        <Route
          path="/customer/order"
          render={(routeProps) => <OrderDetail profileData={profileData} {...routeProps} />}
        />
        <Route path={`/${ADMIN_URL}/`} render={(routeProps) => <Admin profileData={profileData} {...routeProps} />} />
        <Route
          path="/employee/builds/:publicHash"
          render={(routeProps) => <MobileBuilds profileData={profileData} {...routeProps} />}
        />
        <Route path="/customer/team" render={(routeProps) => <Team profileData={profileData} {...routeProps} />} />
        <Route path="/authentication" component={Authentication} exact />
        <Route
          path="/500-error/"
          render={() => {
            return (
              <ErrorPage
                nameOfError="Error 500"
                errorTitle="Internal server error"
                recommendation="We’re working towards creating something better. We won’t be long. Please try again later."
                errorClick={() => window.location.reload()}
                errorButton="Refresh"
              />
            );
          }}
        />
        <Route
          path="/test-translation/"
          render={() => {
            return (
              <>
                <h1>{t('title')}</h1>
                <LanguageSwitcher />
              </>
            );
          }}
        />
        <Route
          path="/400-error/"
          render={() => {
            return (
              <ErrorPage
                nameOfError="Error 400"
                errorTitle="Bad request"
                recommendation="Server detected a syntax error in the client request. Please try again later."
                errorClick={() => (window.location.href = '/')}
                errorButton="Go home"
              />
            );
          }}
        />
        <Route
          path="/404-error/"
          render={() => {
            return (
              <ErrorPage
                nameOfError="Error 404"
                errorTitle="Something went wrong"
                recommendation="The page you were looking for doesn’t exist. You may have mistyped the address or the page may have moved."
                errorClick={() => window.history.back()}
                errorButton="Go back"
              />
            );
          }}
        />
      </AppSwitch>
      <Switch>
        <Route path="/" component={Profile} />
      </Switch>
    </StoreProvider>
  );
});

const AppWithProviders = () => (
  // Hack to support backend base url
  <React.Suspense fallback="Loading...">
    <Router>
      <ApolloProvider client={Client}>
        <Route path="/" component={App} />
      </ApolloProvider>
    </Router>
  </React.Suspense>
);

export default AppWithProviders;
